import { ApolloQueryResult, FetchResult, gql, QueryHookOptions, QueryResult } from '@apollo/client';
import { ClientLogger } from '../client-logger';
import {
  teamQuery,
  teamCreateMutation,
  TeamCreateInput,
  teamCreateMutationVariables,
  teamsQuery,
  teamsQueryVariables,
  teamUpdateMutation,
  TeamUpdateInput,
  teamUpdateMutationVariables,
  teamQueryVariables,
  Team,
  findManyTeamsByPersonOrgIdsQuery,
} from './verid-api-types';
//import { teams_teams_edges_node } from './api-types-parts';
import { useVeridApi } from './hybrid-apollo-client';

const DEBUG = false;

export type TeamEditData = Omit<Team, 'id' | 'createdAt' | 'updatedAt'>;

export const TEAM_FIELDS = gql`
  fragment TeamFragment on Team {
    id
    createdAt
    updatedAt
    name
    description
    orgId
    status
    idpGroupId
  }
`;

export function useTeamService() {
  const api = useVeridApi();
  const teamsQuery = gql`
    ${TEAM_FIELDS}
    query teams($name: String, $orgId: String!, $orderBy: TeamOrder, $first: Int, $after: String) {
      teams(query: { name: $name, orgId: $orgId }, orderBy: $orderBy, first: $first, after: $after) {
        edges {
          cursor
          node {
            ...TeamFragment
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
        totalCount
      }
    }
  `;
  return {
    useTeamQuery(options: QueryHookOptions<teamsQuery, teamsQueryVariables> | undefined) {
      function fct(): QueryResult<teamsQuery, teamsQueryVariables> {
        return api.useQuery(teamsQuery, options);
      }
      return fct;
    },

    async teams(options: QueryHookOptions<teamsQuery, teamsQueryVariables> | undefined) {
      return api.query<teamsQuery, teamsQueryVariables>({
        query: teamsQuery,
        variables: { orgId: options?.variables?.orgId || '' },
      });
    },

    async team(id: string): Promise<ApolloQueryResult<teamQuery>> {
      return api.query<teamQuery, teamQueryVariables>({
        query: gql`
          ${TEAM_FIELDS}
          query team($id: String!) {
            team(id: $id) {
              ...TeamFragment
            }
          }
        `,
        variables: { id },
        fetchPolicy: 'network-only',
      });
    },

    async findManyTeamsByPersonOrgIds(): Promise<ApolloQueryResult<findManyTeamsByPersonOrgIdsQuery>> {
      return api.query({
        query: gql`
          ${TEAM_FIELDS}
          query findManyTeamsByPersonOrgIds {
            findManyTeamsByPersonOrgIds {
              ...TeamFragment
            }
          }
        `,
        variables: {},
        fetchPolicy: 'network-only',
      });
    },

    async teamCreate(team: TeamCreateInput): Promise<FetchResult<teamCreateMutation>> {
      const retVal = await api.mutate<teamCreateMutation, teamCreateMutationVariables>({
        mutation: gql`
          mutation teamCreate($data: TeamCreateInput!) {
            teamCreate(data: $data) {
              id
            }
          }
        `,
        variables: { data: team },
        refetchQueries: ['teams'],
      });
      await api.refreshMe();
      return retVal;
    },

    async teamUpdate(orgId: string, org: TeamUpdateInput): Promise<FetchResult<teamUpdateMutation>> {
      return api.mutate<teamUpdateMutation, teamUpdateMutationVariables>({
        mutation: gql`
          mutation teamUpdate($id: String!, $data: TeamUpdateInput!) {
            teamUpdate(id: $id, data: $data) {
              id
            }
          }
        `,
        variables: { data: org, id: orgId },
        refetchQueries: ['teams'],
      });
    },
  };
}
